import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { XStack, YStack } from "tamagui";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageScaffold, AdminStatusPill } from "@/components/AdminScaffold";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppLoadingState,
  AppSection,
  AppSectionHeader,
  AppText,
  AppTextArea,
  kindredFontFamilies,
} from "../../ui-foundation";

type AdminDeletionRequest = {
  id: string;
  user_id: string;
  contact_email: string | null;
  details: string | null;
  status: "pending" | "in_review" | "completed" | "rejected" | "cancelled";
  admin_note: string | null;
  created_at: string;
  updated_at: string;
  reviewed_at: string | null;
  profile: {
    display_name: string | null;
    avatar_url?: string | null;
    postcode: string | null;
  } | null;
  reviewer: {
    display_name: string | null;
  } | null;
};

const statusTone: Record<AdminDeletionRequest["status"], "primary" | "secondary" | "success" | "danger" | "neutral"> =
  {
    pending: "primary",
    in_review: "secondary",
    completed: "success",
    rejected: "danger",
    cancelled: "neutral",
  };

const getStorageCleanupFollowUpMessage = (warningCount: number) =>
  warningCount === 1
    ? "Account deleted. One storage cleanup step still needs follow-up because some objects could not be removed automatically."
    : `Account deleted. ${warningCount} storage cleanup steps still need follow-up because some objects could not be removed automatically.`;

const AdminAccountDeletion = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<AdminDeletionRequest[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState<string | null>(null);

  const loadRequests = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("account_deletion_requests")
      .select(
        "id, user_id, contact_email, details, status, admin_note, created_at, updated_at, reviewed_at, profile:profiles!account_deletion_requests_user_id_fkey(display_name, avatar_url), reviewer:profiles!account_deletion_requests_reviewed_by_fkey(display_name)",
      )
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Could not load deletion requests.");
      setLoading(false);
      return;
    }

    const rows = (data ?? []) as AdminDeletionRequest[];
    const profileIds = Array.from(new Set(rows.map((row) => row.user_id)));
    const { data: adminProfiles } = profileIds.length
      ? await supabase.from("admin_profiles").select("id, postcode").in("id", profileIds)
      : { data: [] as Array<{ id: string; postcode: string | null }> };
    const postcodeByUserId = new Map((adminProfiles ?? []).map((profile) => [profile.id, profile.postcode]));
    const nextRows = rows.map((row) => ({
      ...row,
      profile: row.profile
        ? {
            ...row.profile,
            postcode: postcodeByUserId.get(row.user_id) ?? null,
          }
        : null,
    }));

    setRequests(nextRows);
    setNotes(Object.fromEntries(nextRows.map((row) => [row.id, row.admin_note ?? ""])));
    setLoading(false);
  };

  useEffect(() => {
    void loadRequests();
  }, []);

  const groupedRequests = useMemo(
    () => ({
      open: requests.filter((request) => request.status === "pending" || request.status === "in_review"),
      history: requests.filter((request) => request.status !== "pending" && request.status !== "in_review"),
    }),
    [requests],
  );

  const updateStatus = async (requestId: string, status: AdminDeletionRequest["status"]) => {
    if (!user?.id) return;

    setWorkingId(requestId);
    const trimmedNote = notes[requestId]?.trim() || null;
    const result =
      status === "completed"
        ? await supabase.functions.invoke("complete-account-deletion-with-storage", {
            body: {
              requestId,
              noteText: trimmedNote,
            },
          })
        : await supabase
            .from("account_deletion_requests")
            .update({
              status,
              admin_note: trimmedNote,
              reviewed_by: user.id,
              reviewed_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .eq("id", requestId);
    setWorkingId(null);

    if (result.error) {
      toast.error(result.error.message || "Could not update that request.");
      return;
    }

    const storageWarningCount =
      status === "completed" && result.data && typeof result.data === "object" && "storageDeletionWarnings" in result.data
        ? Array.isArray(result.data.storageDeletionWarnings)
          ? result.data.storageDeletionWarnings.length
          : 0
        : 0;

    if (status === "completed" && storageWarningCount > 0) {
      toast.success(getStorageCleanupFollowUpMessage(storageWarningCount));
    } else {
      toast.success(
        status === "completed"
          ? "Account deleted and storage cleaned up."
          : "Deletion request updated.",
      );
    }
    void loadRequests();
  };

  const RequestCard = ({ request }: { request: AdminDeletionRequest }) => (
    <AppCard>
      <AppCardBody>
        <XStack alignItems="flex-start" justifyContent="space-between" gap="$sm" flexWrap="wrap">
          <YStack flex={1} minWidth={220} gap={4}>
            <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$lg" lineHeight="$lg">
              {request.profile?.display_name || "Kindred member"}
            </AppText>
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
              {request.contact_email || "No email provided"}
              {request.profile?.postcode ? ` · ${request.profile.postcode}` : ""}
            </AppText>
            <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
              Requested {format(new Date(request.created_at), "d MMM yyyy, HH:mm")}
            </AppText>
          </YStack>
          <AdminStatusPill tone={statusTone[request.status]}>
            {request.status.replace("_", " ")}
          </AdminStatusPill>
        </XStack>

        {request.details ? (
          <AppCard backgroundColor="$background">
            <AppCardBody>
              <AppText
                color="$primary"
                fontFamily={kindredFontFamilies.sansBold}
                fontSize="$xs"
                lineHeight="$sm"
                textTransform="uppercase"
                letterSpacing={1.2}
              >
                Member note
              </AppText>
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md" whiteSpace="pre-wrap">
                {request.details}
              </AppText>
            </AppCardBody>
          </AppCard>
        ) : null}

        <YStack gap="$xs">
          <AppText
            color="$primary"
            fontFamily={kindredFontFamilies.sansBold}
            fontSize="$xs"
            lineHeight="$sm"
            textTransform="uppercase"
            letterSpacing={1.2}
          >
            Internal note
          </AppText>
          <AppTextArea
            value={notes[request.id] ?? ""}
            onChangeText={(value) =>
              setNotes((current) => ({ ...current, [request.id]: value.slice(0, 500) }))
            }
            placeholder="What did you verify or tell the member?"
            minHeight={116}
          />
        </YStack>

        {request.status === "pending" || request.status === "in_review" ? (
          <AppButtonRow>
            {request.status === "pending" ? (
              <AppButton
                type="button"
                tone="outline"
                size="sm"
                onPress={() => updateStatus(request.id, "in_review")}
                disabled={workingId === request.id}
              >
                {workingId === request.id ? "Saving..." : "Mark in review"}
              </AppButton>
            ) : null}
            <AppButton
              type="button"
              size="sm"
              onPress={() => updateStatus(request.id, "completed")}
              disabled={workingId === request.id}
            >
              {workingId === request.id ? "Deleting..." : "Complete deletion"}
            </AppButton>
            <AppButton
              type="button"
              tone="outline"
              size="sm"
              onPress={() => updateStatus(request.id, "rejected")}
              disabled={workingId === request.id}
            >
              Reject request
            </AppButton>
          </AppButtonRow>
        ) : request.reviewer?.display_name ? (
          <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
            Last updated by {request.reviewer.display_name}
            {request.reviewed_at ? ` on ${format(new Date(request.reviewed_at), "d MMM yyyy, HH:mm")}` : ""}
          </AppText>
        ) : null}
      </AppCardBody>
    </AppCard>
  );

  return (
    <AdminPageScaffold
      title="Account deletion"
      subtitle="Review member deletion requests, record internal notes, and close the loop cleanly."
      fallbackPath="/admin"
    >
      {loading ? (
        <AppLoadingState
          title="Loading deletion requests"
          body="Pulling the latest open requests and recent history."
        />
      ) : (
        <YStack gap="$md">
          <AppSection>
            <AppSectionHeader
              title="Open requests"
              subtitle="Deletion requests still waiting for admin action."
            />
            {groupedRequests.open.length ? (
              <YStack gap="$sm">
                {groupedRequests.open.map((request) => (
                  <RequestCard key={request.id} request={request} />
                ))}
              </YStack>
            ) : (
              <AppEmptyState
                title="No open deletion requests"
                body="There is nothing waiting for review right now."
              />
            )}
          </AppSection>

          <AppSection>
            <AppSectionHeader
              title="Recent history"
              subtitle="Completed, rejected, or cancelled requests."
            />
            {groupedRequests.history.length ? (
              <YStack gap="$sm">
                {groupedRequests.history.map((request) => (
                  <RequestCard key={request.id} request={request} />
                ))}
              </YStack>
            ) : (
              <AppEmptyState
                title="No completed requests yet"
                body="Closed deletion requests will appear here once admins start processing them."
              />
            )}
          </AppSection>
        </YStack>
      )}
    </AdminPageScaffold>
  );
};

export default AdminAccountDeletion;
